import type { Condition } from 'features/charts-v2/types/yogiql'
import {
  fromYogiQLFilters,
  type StoredPanelReadContext,
  warnYogiQLFallback,
} from 'features/charts-v2/utils'
import type {
  StoredFilterState,
  YogiQLStoredFilterState,
} from 'features/dashboards-v2/types'
import {
  type DashboardFilterStateV2,
  emptyDashboardFilterStateV2,
} from 'features/dashboards-v2/types/filters-v2'

// Read side of Dashboards 2.1's `stored-widget-filters` (2.1 writes this form
// under `enhanced_dialect`; 2.0 only reads it). Keep in step with 2.1's copy.

/** Readers dispatch per item on `request`; a legacy state never has one. */
export const isYogiQLStoredFilterState = (
  value: unknown,
): value is YogiQLStoredFilterState =>
  typeof value === 'object' && value !== null && 'request' in value

/**
 * A widget tile's stored `filterValues` as the page edits them. A legacy state
 * passes through; a YogiQL one reads its conditions back against the hub's
 * filter options, with no date condition as "inherit" (`dateRange: null`).
 * Conditions that do not read (the registry moved on, or the filter options
 * did not load: a null context) give the empty state with a warning, so the
 * tile renders unfiltered rather than failing the dashboard; values the hub no
 * longer has drop out with a warning naming them. The stored hash is untouched.
 */
export function fromStoredFilterState(
  stored: StoredFilterState | undefined,
  context: StoredPanelReadContext | null,
): DashboardFilterStateV2 {
  if (!stored) return emptyDashboardFilterStateV2
  if (!isYogiQLStoredFilterState(stored)) return stored
  const decoded = context
    ? readFilters(stored.request.filters, context)
    : ({ ok: false, reason: 'the filter options did not load' } as const)
  if (!decoded.ok) {
    warnYogiQLFallback(`stored widget filters did not load: ${decoded.reason}`)
    return emptyDashboardFilterStateV2
  }
  if (decoded.dropped.length) {
    warnYogiQLFallback(
      `stored filter values no longer in this hub: ${decoded.dropped.join(', ')}`,
    )
  }
  return decoded.state
}

type FilterStateReading =
  | { ok: true; state: DashboardFilterStateV2; dropped: string[] }
  | { ok: false; reason: string }

function readFilters(
  conditions: Condition[],
  context: StoredPanelReadContext,
): FilterStateReading {
  const decoded = fromYogiQLFilters(conditions, context.filterOptions)
  if (!decoded.ok) return decoded
  const { dateRange, ...rest } = decoded.fields
  return {
    ok: true,
    state: {
      ...rest,
      dateRange: dateRange.startDate || dateRange.endDate ? dateRange : null,
    },
    dropped: decoded.dropped,
  }
}
